<template>
  <div class="container-fluid p-4">
    <!-- Loading ekranı -->
    <div v-if="loading" class="loading-overlay">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p>Yükleniyor...</p>
    </div>
    <b-row>
      <!-- Kategori Listesi -->
      <b-col cols="3">
        <b-card title="Kategoriler">
          <b-list-group>
            <draggable v-model="categories" @end="updateCategoryOrder" item-key="id">
              <b-list-group-item
                  v-for="category in categories"
                  :key="category.id"
                  :active="selectedCategory && selectedCategory.id === category.id"
                  @click="selectCategory(category)"
                  class="d-flex justify-content-between align-items-center"
              >
                {{ category.name }}
                <div>
                  <b-icon icon="pencil" @click.stop="showEdit(category)" class="mr-2 cursor-pointer" />
                  <b-icon icon="trash" variant="danger" @click.stop="showDelete(category.id)" class="cursor-pointer" />
                </div>
              </b-list-group-item>
            </draggable>
          </b-list-group>
          <b-button block class="mt-2" variant="success" @click="showAddModal = true">+ Kategori Ekle</b-button>
        </b-card>
      </b-col>

      <b-col cols="4">
        <b-card v-if="selectedCategory" :title="`Kategoriye Ait Ürünler`">
          <b-list-group v-if="selectedCategory">
            <draggable v-model="selectedCategoryProducts" @end="updateCategoryProductOrder" item-key="id">
              <b-list-group-item
                  v-for="product in selectedCategoryProducts"
                  :key="product.id"
                  class="d-flex justify-content-between"
              >
                {{ product.productName }}
                <b-button size="sm" variant="danger" @click="removeProductFromCategory(product.productId)">Çıkar</b-button>
              </b-list-group-item>
            </draggable>
          </b-list-group>
          <p v-else>Ürünleri görüntülemek için kategori seçin.</p>
        </b-card>
      </b-col>

      <b-col cols="5">
        <b-card title="Ürün Ara ve Ekle" v-if="selectedCategory">
          <div class="container">
            <vue-good-table
                mode="remote"
                @on-column-filter="onColumnFilter($event.columnFilters)"
                @on-page-change="onPageChange"
                @on-per-page-change="onPerPageChange"
                :rows="rows"
                :columns="columns"
                :totalRows="totalRecords"
                :line-numbers="true"
                :pagination-options="{
                  enabled: true,
                  perPage: serverParams.perPage,
                  perPageDropdown: [5, 10, 20, 50],
                  dropdownAllowAll: false,
                  mode: 'records',
                  nextLabel: 'Sonraki',
                  prevLabel: 'Önceki',
                  rowsPerPageLabel: 'Sayfa başına satır',
                  ofLabel: '/',
                  pageLabel: 'sayfa',
                  allLabel: 'Tümü'
                }"
            >
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'actions'">
                  <div class="d-flex justify-content-center gap-2 px-2">
                    <b-button v-if="!selectedCategoryProductIds.includes(props.formattedRow.id)" size="sm" variant="primary" @click="addProductToCategory(props.formattedRow.id)">Ekle</b-button>
                    <b-button v-if="selectedCategoryProductIds.includes(props.formattedRow.id)" size="sm" variant="danger" @click="removeProductFromCategory(props.formattedRow.id)">Çıkar</b-button>
                  </div>
                </span>
                <span v-else>
                  {{props.formattedRow[props.column.field]}}
                </span>
              </template>
            </vue-good-table>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Kategori Ekle Modalı -->
    <b-modal v-model="showAddModal" title="Yeni Kategori Ekle" @ok="createCategory">
      <b-form-group label="Kategori Adı">
        <b-form-input v-model="newCategoryName" required />
      </b-form-group>
    </b-modal>

    <!-- Kategori Düzenle Modalı -->
    <b-modal v-model="showEditModal" title="Kategori Güncelle" @ok="editCategory">
      <b-form-group label="Kategori Adı">
        <b-form-input v-model="editedCategoryName" required />
      </b-form-group>
    </b-modal>

    <!-- Kategori Sil Modalı -->
    <b-modal v-model="showDeleteModal" title="Kategoriyi Sil"
             ok-title="Evet, Sil"
             cancel-title="Vazgeç"
             ok-variant="danger"
             @ok="deleteCategory">
      <p class="my-2">Bu kategoriyi silmek istediğinizden emin misiniz?</p>
    </b-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import CategoryService from "@/service/CategoryService";
import ProductService from "@/service/ProductService";

export default {
  name: 'AdminCategoryManagement',
  components: {
    draggable,
  },
  data() {
    return {
      categories: [],
      selectedCategory: null,
      selectedCategoryProducts: [],
      selectedCategoryProductIds: [],
      productSearch: '',
      showAddModal: false,
      showEditModal: false,
      showDeleteModal: false,
      newCategoryName: '',
      editedCategoryName: '',
      editCategoryRecord: undefined,
      deleteId: undefined,
      totalRecords: 0,
      loading: false,
      columns: [
        { field: 'id', hidden: true },
        { label: 'Ürün Adı', field: 'name', sortable: false, filterOptions: { enabled: true, placeholder: 'Ürün Adına Göre Filtrele', trigger: 'enter' } },
        { field: 'actions', sortable: false },
      ],
      serverParams: {
        columnFilters: {},
        sort: { field: '', type: '' },
        page: 1,
        perPage: 10,
      },
      rows: [],
    };
  },
  methods: {
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },
    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadProducts(this.serverParams);
    },
    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage });
      this.loadProducts(this.serverParams);
    },
    onColumnFilter(params) {
      this.updateParams({ columnFilters: params });
      this.loadProducts(this.serverParams);
    },
    async loadProducts(params) {
      this.loading = true;
      try {
        const response = await ProductService.getAdminList(params);
        this.rows = response.data;
        this.totalRecords = response.total;
      } catch (error) {
        console.error("Listeleme sırasında hata oluştu:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchInitialData() {
      this.loading = true;
      try {
        this.categories = await CategoryService.getCategories();
        const response = await ProductService.getAdminList({ columnFilters: {} });
        this.rows = response.data;
        this.totalRecords = response.total;
      } catch (error) {
        console.error("Ürün ve kategoriler listelenirken hata oluştu:", error);
      } finally {
        this.loading = false;
      }
    },
    async selectCategory(category) {
      if (this.selectedCategory && this.selectedCategory.id === category.id) {
        this.selectedCategory = undefined;
        return;
      }
      this.selectedCategory = category;
      this.loading = true;
      try {
        this.selectedCategoryProducts = await CategoryService.getProductsByCategoryId(category.id);
      } catch (error) {
        console.error("Ürün listenirken hata oluştu:", error);
      } finally {
        this.loading = false;
      }
      this.selectedCategoryProductIds = this.selectedCategoryProducts.map(p => p.productId);
    },
    async refreshCategory(category) {
      this.selectedCategory = category;
      this.loading = true;
      try {
        this.selectedCategoryProducts = await CategoryService.getProductsByCategoryId(category.id);
      } catch (error) {
        console.error("Ürün listenirken hata oluştu:", error);
      } finally {
        this.loading = false;
      }
      this.selectedCategoryProductIds = this.selectedCategoryProducts.map(p => p.productId);
    },
    async updateCategoryOrder() {
      this.loading = true;
      try {
        await CategoryService.updateCategoryOrder({ categoryIds: this.categories.map(p => p.id) });
      } catch (error) {
        this.$alert(this, error.message);
      } finally {
        this.loading = false;
      }
    },
    async updateCategoryProductOrder() {
      this.loading = true;
      try {
        await CategoryService.updateCategoryProductOrder({ categoryId: this.selectedCategory.id, productIds: this.selectedCategoryProducts.map(p => p.productId) });
      } catch (error) {
        this.$alert(this, error.message);
      } finally {
        this.loading = false;
      }
    },
    async addProductToCategory(productId) {
      this.loading = true;
      try {
        await CategoryService.addProduct({ categoryId: this.selectedCategory.id, productId: productId });
      } catch (error) {
        this.$alert(this, error.message);
      } finally {
        this.loading = false;
      }
      await this.refreshCategory(this.selectedCategory);
    },
    async removeProductFromCategory(productId) {
      this.loading = true;
      try {
        await CategoryService.removeProduct({ categoryId: this.selectedCategory.id, productId: productId });
      } catch (error) {
        this.$alert(this, error.message);
      } finally {
        this.loading = false;
      }
      await this.refreshCategory(this.selectedCategory);
    },
    async createCategory() {
      if (!this.newCategoryName) return;
      this.loading = true;
      try {
        await CategoryService.create({ name: this.newCategoryName });
      } catch (error) {
        this.$alert(this, error.message);
      } finally {
        this.loading = false;
      }
      this.newCategoryName = '';
      this.showAddModal = false;
      await this.fetchInitialData();
    },
    async showEdit(category) {
      this.editCategoryRecord = category;
      this.editedCategoryName = category.name;
      this.showEditModal = true;
    },
    async editCategory() {
      if (!this.editedCategoryName) return;
      this.loading = true;
      try {
        await CategoryService.update({ id: this.editCategoryRecord.id, name: this.editedCategoryName });
      } catch (error) {
        this.$alert(this, error.message);
      } finally {
        this.loading = false;
      }
      this.editedCategoryName = '';
      this.showEditModal = false;
      await this.fetchInitialData();
    },
    async showDelete(categoryId) {
      this.deleteId = categoryId;
      this.showDeleteModal = true;
    },
    async deleteCategory() {
      this.loading = true;
      try {
        await CategoryService.delete(this.deleteId);
      } catch (error) {
        this.$alert(this, error.message);
      } finally {
        this.loading = false;
      }
      this.deleteId = undefined;
      if (this.selectedCategory && this.selectedCategory.id === this.deleteId) {
        this.selectedCategory = null;
        this.selectedCategoryProducts = [];
      }
      await this.fetchInitialData();
    },
  },
  mounted() {
    this.fetchInitialData();
  },
};
</script>
